import { isNil } from "lodash";

import { KEY } from "@/react/components/table/useTableMutations";
import Debug from "@/react/debug";
import { HttpMethods } from "@/react/http";
import { createCustomNotifier } from "@/react/notifications";
import { emailBaseApi } from "@/react/services/api";
import { autoIdJSON64EncodedTransform } from "@/react/services/api/transforms";
import { handleMutationResponse } from "@/react/services/api/handleMutationResponse";

import { EmailSearchMethods } from "./constants";

const SUPPRESSION_PATH = "suppression";
const BOUNCES_URL = `${SUPPRESSION_PATH}/bounces`;
const BLOCKS_URL = `${SUPPRESSION_PATH}/blocks`;
const SPAM_URL = `${SUPPRESSION_PATH}/spam_reports`;

const BLOCKS = "suppression-blocks";
const BOUNCES = "suppression-bounces";
const SPAM = "suppression-spam-reports";

const emailSuppressionApi = emailBaseApi.injectEndpoints({
  endpoints: (build) => ({
    getBounces: build.query({
      query: (options) => ({
        url: `${BOUNCES_URL}${serializeOptions(options)}`,
        method: HttpMethods.GET,
      }),
      transformResponse: autoIdJSON64EncodedTransform({ prefixKey: "email" }),
      providesTags: [BOUNCES],
    }),
    getBlocks: build.query({
      query: (options) => ({
        url: `${BLOCKS_URL}${serializeOptions(options)}`,
        method: HttpMethods.GET,
      }),
      transformResponse: autoIdJSON64EncodedTransform({ prefixKey: "email" }),
      providesTags: [BLOCKS],
    }),
    getSpam: build.query({
      query: (options) => ({
        url: `${SPAM_URL}${serializeOptions(options)}`,
        method: HttpMethods.GET,
      }),
      transformResponse: autoIdJSON64EncodedTransform({ prefixKey: "email" }),
      providesTags: [SPAM],
    }),
    unsuppressBounces: build.mutation({
      query: (emails) => ({
        url: `${BOUNCES_URL}`,
        method: HttpMethods.DELETE,
        body: emails,
      }),
      invalidatesTags: [BOUNCES],
    }),
    unsuppressBlocks: build.mutation({
      query: (emails) => ({
        url: `${BLOCKS_URL}`,
        method: HttpMethods.DELETE,
        body: emails,
      }),
      invalidatesTags: [BLOCKS],
    }),
    unsuppressSpam: build.mutation({
      query: (emails) => ({
        url: `${SPAM_URL}`,
        method: HttpMethods.DELETE,
        body: emails,
      }),
      invalidatesTags: [SPAM],
    }),
  }),
});

const serializeOptions = (options) => {
  if (
    isNil(options.searchMethod) ||
    options.searchMethod === EmailSearchMethods.CONTAINS
  ) {
    const queryParams = new URLSearchParams();
    if ("from" in options) {
      queryParams.append("from", options.from);
    }
    if ("to" in options) {
      queryParams.append("to", options.to);
    }
    if ("size" in options) {
      queryParams.append("size", options.size);
    }
    if ("page" in options) {
      queryParams.append("page", options.page);
    }
    if ("searchTerm" in options) {
      queryParams.append("emailQuery", options.searchTerm);
    }
    return "?" + queryParams.toString();
  } else {
    if (options.searchMethod !== EmailSearchMethods.EXACT) {
      Debug.error(
        `Invalid search method '${options.searchMethod}', using 'exact' search`
      );
    }
    return "/" + options.searchTerm;
  }
};

export const handleUnsuppressResponse = ({
  rowsToDelete,
  setRowsToDelete,
  setData,
  dataKey,
}) =>
  handleMutationResponse({
    rows: rowsToDelete,
    setRows: setRowsToDelete,
    setData,
    responseMappingKey: dataKey,
    idKey: KEY,
    notifyContent: createCustomNotifier({
      operation: `removed from suppression list`,
      singular: "email",
      plural: "emails",
    }),
    isVoidResponse: true,
  });

export const {
  useGetBouncesQuery,
  useLazyGetBouncesQuery,
  useGetBlocksQuery,
  useLazyGetBlocksQuery,
  useGetSpamQuery,
  useLazyGetSpamQuery,
  useUnsuppressBouncesMutation,
  useUnsuppressBlocksMutation,
  useUnsuppressSpamMutation,
} = emailSuppressionApi;
